import React, { useState, useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { authenticatedFetchJson } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import logger from '../../utils/logger'
import styles from './StripeCardForm.module.scss'

// Stripe publishable keys (pk_test_* / pk_live_*) are designed to be embedded
// in client code; bundling at build-time via REACT_APP_* is the simplest path.
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null

const PAD_INTRO = (
  <div className={styles.padIntro}>
    <h3 className={styles.padIntroTitle}>Pre-Authorized Debit (PAD)</h3>
    <p>
      By submitting this form, you authorize Veta Virtual to debit the bank account specified for
      any amount owed for charges arising from your use of our services and/or purchase of products
      from us, until this authorization is revoked. You may amend or cancel this authorization at
      any time, provided you give Veta Virtual at least 30&nbsp;days notice.
    </p>
    <p className={styles.padIntroSecondary}>
      This is a Business PAD authorization under Payments Canada Rule&nbsp;H1. For details on
      revocation, contact us or see{' '}
      <a href="https://www.payments.ca" target="_blank" rel="noopener noreferrer">
        payments.ca
      </a>
      .
    </p>
  </div>
)

const InnerForm = ({
  paymentMethodType,
  returnUrl = null,
  onSaved,
  onError = null,
  defaultName = null,
  defaultEmail = null,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [success, setSuccess] = useState(false)

  const submitLabel = paymentMethodType === 'acss_debit' ? 'Authorize PAD' : 'Save card'

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault()
      if (!stripe || !elements) return
      setSubmitting(true)
      setSubmitError(null)
      try {
        const result = await stripe.confirmSetup({
          elements,
          confirmParams: returnUrl ? { return_url: returnUrl } : undefined,
          redirect: 'if_required',
        })
        if (result.error) {
          setSubmitError(result.error.message)
          if (onError) onError(result.error.message)
        } else {
          setSuccess(true)
          onSaved()
        }
      } catch (err) {
        logger.error('confirmSetup failed:', err)
        setSubmitError('An unexpected error occurred. Please try again.')
        if (onError) onError(err.message)
      } finally {
        setSubmitting(false)
      }
    },
    [stripe, elements, returnUrl, onSaved, onError],
  )

  if (success) {
    return (
      <div className={styles.successBox} data-testid="stripe-form-success">
        <FontAwesomeIcon icon="check-circle" className={styles.successIcon} />
        <p className={styles.successText}>
          {paymentMethodType === 'acss_debit'
            ? 'PAD authorization saved. Future invoices will be debited from this account.'
            : 'Card saved successfully. Your payment method has been updated.'}
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form} data-testid="stripe-form">
      {paymentMethodType === 'acss_debit' && PAD_INTRO}

      <div className={styles.elementBox}>
        <PaymentElement
          options={{
            layout: 'tabs',
            defaultValues: {
              billingDetails: {
                name: defaultName || undefined,
                email: defaultEmail || undefined,
              },
            },
          }}
        />
      </div>

      {submitError && <p className={styles.errorText}>{submitError}</p>}

      <button
        type="submit"
        className={styles.saveButton}
        disabled={!stripe || !elements || submitting}
        data-testid="stripe-form-submit"
      >
        {submitting ? 'Saving…' : submitLabel}
      </button>

      <div className={styles.stripeFooter}>
        <span>Powered by </span>
        <FontAwesomeIcon icon={['fab', 'stripe']} className={styles.stripeLogo} />
      </div>
    </form>
  )
}

InnerForm.propTypes = {
  paymentMethodType: PropTypes.oneOf(['card', 'acss_debit']).isRequired,
  returnUrl: PropTypes.string,
  onSaved: PropTypes.func.isRequired,
  onError: PropTypes.func,
  defaultName: PropTypes.string,
  defaultEmail: PropTypes.string,
}


const StripeCardForm = ({
  billingCustomerId,
  paymentMethodType = 'card',
  onSaved,
  onError = null,
  returnUrl = null,
  defaultName = null,
  defaultEmail = null,
}) => {
  const [clientSecret, setClientSecret] = useState(null)
  const [bootstrapError, setBootstrapError] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const bootstrap = async () => {
      try {
        const intent = await authenticatedFetchJson(
          apiEndpoints.billingCreateSetupIntent(billingCustomerId),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ payment_method_type: paymentMethodType }),
          },
        )
        if (cancelled) return
        setClientSecret(intent?.data?.client_secret || null)
        setLoading(false)
      } catch (err) {
        if (cancelled) return
        logger.error('Stripe form bootstrap failed:', err)
        setBootstrapError('Unable to initialize payment form. Please try again.')
        setLoading(false)
        if (onError) onError(err.message)
      }
    }

    bootstrap()

    return () => {
      cancelled = true
    }
  }, [billingCustomerId, paymentMethodType, onError])

  const elementsOptions = useMemo(() => {
    if (!clientSecret) return null
    return {
      clientSecret,
      appearance: { theme: 'stripe' },
    }
  }, [clientSecret])

  if (!stripePromise) {
    return (
      <div className={styles.unavailable} data-testid="stripe-unavailable">
        <p className={styles.unavailableText}>
          Stripe is not configured — set REACT_APP_STRIPE_PUBLISHABLE_KEY.
        </p>
      </div>
    )
  }

  if (loading) {
    return <p className={styles.loadingText}>Preparing form…</p>
  }

  if (bootstrapError) {
    return (
      <div className={styles.unavailable} data-testid="stripe-unavailable">
        <p className={styles.unavailableText}>{bootstrapError}</p>
      </div>
    )
  }

  if (!clientSecret || !elementsOptions) return null

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <InnerForm
        paymentMethodType={paymentMethodType}
        returnUrl={returnUrl}
        onSaved={onSaved}
        onError={onError}
        defaultName={defaultName}
        defaultEmail={defaultEmail}
      />
    </Elements>
  )
}

StripeCardForm.propTypes = {
  billingCustomerId: PropTypes.number.isRequired,
  paymentMethodType: PropTypes.oneOf(['card', 'acss_debit']),
  onSaved: PropTypes.func.isRequired,
  onError: PropTypes.func,
  returnUrl: PropTypes.string,
  defaultName: PropTypes.string,
  defaultEmail: PropTypes.string,
}


export default StripeCardForm
